/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
        // A ordenação escolhida vale com filtro aplicado também. Sem mandar
        // isto, o backend caía no `last_activity_at desc` fixo e o seletor
        // continuava exibindo a opção que o usuário tinha escolhido.
        sort_by: payload.sortBy,
      },
    });
  }

  // `page` era fixo em 1: a busca mostrava no máximo os 25 primeiros
  // resultados e a rolagem não trazia mais nada, sem nenhum aviso.
  search({ q, assigneeType, status, page = 1 }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page,
        assignee_type: assigneeType,
        status,
      },
    });
  }

  // `assumir` diz ao servidor que ALGUÉM APERTOU O BOTÃO de reabrir — e só
  // nesse caso a conversa passa a ser de quem apertou. Conversa que reabre por
  // consequência (cliente volta a escrever, automação, entrega do bot) não
  // manda o sinal e vai para a fila. Ver `should_assign_conversation?`.
  toggleStatus({
    conversationId,
    status,
    snoozedUntil = null,
    assumir = false,
  }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
      assumir,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId, assigneeType }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
      ...(assigneeType ? { assignee_type: assigneeType } : {}),
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  toggleAiPause(conversationId) {
    return axios.post(`${this.url}/${conversationId}/toggle_ai_pause`);
  }

  // advup — fatia 0A: só administrador, com motivo (o servidor confere os dois).
  liberarCongelamento(conversationId, motivo) {
    return axios.post(`${this.url}/${conversationId}/liberar_congelamento`, {
      motivo,
    });
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
